import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Shield, Globe, Server, Lock, MapPin, Save, Loader2, CheckCircle, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { getDataResidency, updateDataResidency, getNISTMapping } from '@/services/dpdpa';

const NIST_COLORS = {
  'Identify-P': 'bg-blue-500', 'Govern-P': 'bg-violet-500', 'Control-P': 'bg-amber-500',
  'Communicate-P': 'bg-emerald-500', 'Protect-P': 'bg-red-500'
};

export default function ResidencyNISTPage() {
  const [residency, setResidency] = useState(null);
  const [nist, setNist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('residency');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [r, n] = await Promise.all([getDataResidency(), getNISTMapping()]);
      setResidency(r);
      setNist(n);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveResidency = async () => {
    try {
      setSaving(true);
      await updateDataResidency(residency);
      toast.success('Data residency settings saved');
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="residency-nist-page">
      <div>
        <h1 className="text-2xl font-bold">Data Residency & NIST Mapping</h1>
        <p className="text-sm opacity-60">Data localization compliance and NIST Privacy Framework alignment</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="residency" data-testid="tab-residency"><MapPin className="w-3.5 h-3.5 mr-1.5" /> Data Residency</TabsTrigger>
          <TabsTrigger value="nist" data-testid="tab-nist"><BarChart3 className="w-3.5 h-3.5 mr-1.5" /> NIST Privacy Framework</TabsTrigger>
        </TabsList>

        <TabsContent value="residency" className="space-y-4 mt-4">
          {residency && (
            <Card data-testid="residency-settings-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4" /> Data Storage & Residency</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Deployment Region</Label>
                    <Input value={residency.deployment_region || ''} onChange={e => setResidency(p => ({ ...p, deployment_region: e.target.value }))} className="mt-1" data-testid="deployment-region-input" />
                  </div>
                  <div>
                    <Label>Cloud Provider</Label>
                    <Input value={residency.cloud_provider || ''} onChange={e => setResidency(p => ({ ...p, cloud_provider: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <Label>Data Center Location</Label>
                    <Input value={residency.data_center_location || ''} onChange={e => setResidency(p => ({ ...p, data_center_location: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <Label>Backup Location</Label>
                    <Input value={residency.backup_location || ''} onChange={e => setResidency(p => ({ ...p, backup_location: e.target.value }))} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <Label>Data Leaves India</Label>
                    <Switch checked={residency.data_leaves_india || false} onCheckedChange={v => setResidency(p => ({ ...p, data_leaves_india: v }))} data-testid="data-leaves-india-switch" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <Label>Encryption at Rest</Label>
                    <Switch checked={residency.encryption_at_rest !== false} onCheckedChange={v => setResidency(p => ({ ...p, encryption_at_rest: v }))} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <Label>Encryption in Transit</Label>
                    <Switch checked={residency.encryption_in_transit !== false} onCheckedChange={v => setResidency(p => ({ ...p, encryption_in_transit: v }))} />
                  </div>
                </div>
                {residency.data_leaves_india && (
                  <div>
                    <Label>Cross-Border Countries (comma-separated)</Label>
                    <Input value={(residency.cross_border_countries || []).join(', ')} onChange={e => setResidency(p => ({ ...p, cross_border_countries: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} className="mt-1" placeholder="e.g., US, Singapore, EU" />
                  </div>
                )}
                <div>
                  <Label>Data Retention Policy</Label>
                  <Input value={residency.data_retention_policy || ''} onChange={e => setResidency(p => ({ ...p, data_retention_policy: e.target.value }))} className="mt-1" />
                </div>
                <Button onClick={handleSaveResidency} disabled={saving} data-testid="save-residency-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Settings
                </Button>

                {/* Compliance Badge */}
                <div className="mt-4 p-4 rounded-lg border-2 border-emerald-500/30 bg-emerald-500/5 flex items-center gap-3" data-testid="residency-badge">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                  <div>
                    <p className="font-medium text-emerald-700">Data Residency: {residency.data_leaves_india ? 'Cross-Border Processing' : 'India-Only'}</p>
                    <p className="text-xs opacity-60">Region: {residency.deployment_region || 'Not configured'} &middot; Provider: {residency.cloud_provider || 'Not configured'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="nist" className="space-y-4 mt-4">
          {nist && (
            <>
              <Card data-testid="nist-overview-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" /> NIST Privacy Framework Alignment</CardTitle>
                  {nist.assessment_source && <p className="text-xs opacity-50">Based on: {nist.assessment_source}</p>}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {Object.entries(nist.nist_mapping || {}).map(([funcId, func]) => (
                      <div key={funcId} className="text-center p-4 rounded-lg border" data-testid={`nist-func-${funcId}`}>
                        <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center text-white text-sm font-bold ${NIST_COLORS[funcId] || 'bg-gray-500'}`}>
                          {func.average_score || 0}
                        </div>
                        <p className="text-sm font-medium">{func.name}</p>
                        <p className="text-[10px] opacity-50">{func.maturity_level}</p>
                        <p className="text-[10px] opacity-40">{func.domains_assessed}/{func.domains_total} domains</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Detailed mapping */}
              {Object.entries(nist.nist_mapping || {}).map(([funcId, func]) => (
                <Card key={funcId} data-testid={`nist-detail-${funcId}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${NIST_COLORS[funcId] || 'bg-gray-500'}`} />
                      <CardTitle className="text-base">{func.name}: {func.description}</CardTitle>
                      <Badge variant="outline">{func.maturity_level}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {func.subcategories?.map(sub => (
                        <div key={sub.id} className="flex items-center justify-between p-2 rounded border text-sm">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] font-mono">{sub.id}</Badge>
                            <span>{sub.name}</span>
                          </div>
                          <span className="text-xs opacity-50">{sub.dpdpa_ref}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-xs opacity-50">DPDPA Domains:</span>
                      {func.dpdpa_domains?.map(d => <Badge key={d} variant="secondary" className="text-[10px]">{d}</Badge>)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
